import { Optional, Provider } from '@angular/core';
import { Store } from '@ngrx/store';

import { DatadogRumService } from '@ninety/platform/services/vendor/datadog';
import { selectCurrentUser } from '@ninety/platform/state/app-entities/users/users-state.selectors';
import { NotificationActions } from '@ninety/platform/state/app-global/notifications/notification.actions';
import {
  ATTACHMENT_LIBRARY,
  ATTACHMENT_PERMISSIONS_USER_STREAM,
  UPLOAD_ERROR_NOTIFIER,
  UPLOAD_FAILURE_REPORTER,
  UploadErrorNotifier,
  UploadFailureReporter,
} from '@ninety/ui/core';
import { LibraryService } from '@ninety/ui/legacy/core/services/library.service';

/**
 * Binds the tokens consumed by `<ninety-file-attachments>` (and its `FileAttachmentsStore`) and the
 * legacy `<ninety-attachments>`, both now packaged in `@ninety/ui/core`, to their platform-backed
 * implementations. The components stay free of `LibraryService`'s transitive graph, of
 * `@ninety/platform/state` and of `@ninety/platform/services` — none of which can be compiled into the
 * public package — so this composition-root wiring is the single place that depends on both sides.
 * (DEV-26530 part 15)
 */
export const ATTACHMENTS_PROVIDERS: Provider[] = [
  { provide: ATTACHMENT_LIBRARY, useExisting: LibraryService },
  {
    provide: ATTACHMENT_PERMISSIONS_USER_STREAM,
    useFactory: (store: Store) => store.select(selectCurrentUser),
    deps: [Store],
  },
  {
    provide: UPLOAD_ERROR_NOTIFIER,
    useFactory:
      (store: Store): UploadErrorNotifier =>
      params =>
        store.dispatch(NotificationActions.showError(params)),
    deps: [Store],
  },
  {
    provide: UPLOAD_FAILURE_REPORTER,
    // Optional: RUM absent degrades to no telemetry, matching the store's former
    // `inject(DatadogRumService, { optional: true })`. A required dep here would instead throw on
    // every attachment component construction.
    useFactory:
      (datadogRum: DatadogRumService | null): UploadFailureReporter =>
      (error, context) =>
        datadogRum?.addError(error, context),
    deps: [[new Optional(), DatadogRumService]],
  },
];
